import type {
  DeliveryCampaignGateRow,
  DeliveryCampaignRow,
  DeliveryCampaignTargetRow,
  DeliveryCandidateRow
} from '../api';

type CampaignProgress = Pick<
  DeliveryCampaignTargetRow,
  | 'targetKey'
  | 'label'
  | 'dependencyKeys'
  | 'rolloutStrategy'
  | 'status'
  | 'errorMessage'
> &
  Partial<Pick<DeliveryCampaignTargetRow, 'metadata'>>;

const finishedStatuses = new Set(['released', 'observed']);

/** Label a delivery-candidate claim without presenting it as runtime health. */
export function deliveryCandidateStatusLabel(
  status: DeliveryCandidateRow['status']
): string {
  if (status === 'deployed') return 'Provider reports deployed';
  if (status === 'proposed') return 'PR open';
  if (status === 'merged') return 'Awaiting deployment';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Keep provider completion distinct from a Kubernetes workload observation. */
export function deliveryCampaignTargetStatusLabel(
  status: DeliveryCampaignTargetRow['status']
): string {
  if (status === 'planned') return 'Not started';
  if (status === 'waiting') return 'Waiting';
  if (status === 'releasing') return 'Releasing';
  if (status === 'released') return 'Released';
  if (status === 'observed') return 'Provider reports deployed';
  if (status === 'cancelled') return 'Cancelled';
  return 'Failed';
}

/** Return unresolved checks for one release unit so its hold-up stays beside its destination. */
export function campaignTargetGateIssues(
  campaign: Pick<DeliveryCampaignRow, 'gates'>,
  target: Pick<DeliveryCampaignTargetRow, 'deliveryCampaignTargetId'>
): DeliveryCampaignGateRow[] {
  return campaign.gates.filter(
    (gate) =>
      gate.deliveryCampaignTargetId === target.deliveryCampaignTargetId &&
      gate.status !== 'ready'
  );
}

/** Keep release-wide checks separate from checks owned by an individual destination. */
export function campaignWideGateIssues(
  campaign: Pick<DeliveryCampaignRow, 'gates'>
): DeliveryCampaignGateRow[] {
  return campaign.gates.filter(
    (gate) => !gate.deliveryCampaignTargetId && gate.status !== 'ready'
  );
}

/** Return targets that can move now without crossing a failed check or unfinished prerequisite. */
export function readyCampaignTargetKeys(
  campaign: Pick<DeliveryCampaignRow, 'targets' | 'gates'>
): string[] {
  if (
    campaign.gates.some(
      (gate) => !gate.deliveryCampaignTargetId && gate.status !== 'ready'
    )
  )
    return [];
  const targetsByKey = new Map(
    campaign.targets.map((target) => [target.targetKey, target])
  );
  return campaign.targets
    .filter((target) => {
      if (!['planned', 'waiting'].includes(target.status)) return false;
      if (
        target.rolloutStrategy === 'manual' &&
        !target.metadata?.['operatorApproval']
      )
        return false;
      if (
        !target.dependencyKeys.every((key) => {
          const prerequisite = targetsByKey.get(key);
          return prerequisite && finishedStatuses.has(prerequisite.status);
        })
      )
        return false;
      const gates = campaign.gates.filter(
        (gate) =>
          gate.deliveryCampaignTargetId === target.deliveryCampaignTargetId
      );
      return gates.length > 0 && gates.every((gate) => gate.status === 'ready');
    })
    .map((target) => target.targetKey);
}

/** Describe the first release action that is ready, blocked, or still underway. */
export function describeCampaignProgress(
  targets: CampaignProgress[],
  campaignStatus?: DeliveryCampaignRow['status']
): string {
  if (campaignStatus === 'cancelled') return 'This release was cancelled.';
  if (
    !targets.length ||
    targets.every((target) => finishedStatuses.has(target.status))
  ) {
    return 'Every release unit has a completed delivery step.';
  }

  const moving = targets.find((target) => target.status === 'releasing');
  if (moving) return `${moving.label} is releasing.`;

  const providerWait = targets.find(
    (target) =>
      target.status === 'waiting' &&
      target.errorMessage?.startsWith('Waiting for provider deployment')
  );
  if (providerWait) {
    const providerStatus =
      providerWait.errorMessage?.match(/\(([^)]+)\)$/)?.[1];
    return `Waiting for GitOps: ${providerWait.label}${providerStatus ? ` is ${providerStatus}` : ''}.`;
  }

  const byKey = new Map(targets.map((target) => [target.targetKey, target]));
  const unfinished = targets.filter(
    (target) => !finishedStatuses.has(target.status)
  );
  const ready = unfinished.filter(
    (target) =>
      (target.rolloutStrategy !== 'manual' ||
        Boolean(target.metadata?.['operatorApproval'])) &&
      !['waiting', 'failed', 'releasing'].includes(target.status) &&
      target.dependencyKeys.every((key) => {
        const dependency = byKey.get(key);
        return dependency && finishedStatuses.has(dependency.status);
      })
  );
  if (ready.length) {
    const labels = ready
      .slice(0, 2)
      .map((target) => target.label)
      .join(' and ');
    const remaining = ready.length - 2;
    const next = `Ready to move: ${labels}${remaining > 0 ? ` and ${remaining} more` : ''}.`;
    const failed = targets.find((target) => target.status === 'failed');
    return failed
      ? `${next} ${failed.label} failed: ${failed.errorMessage || 'the release step failed'}.`
      : next;
  }

  const failed = targets.find((target) => target.status === 'failed');
  if (failed)
    return `Fix ${failed.label}: ${failed.errorMessage || 'the release step failed'}.`;

  const needsProviderCheck = unfinished.find(
    (target) =>
      target.status === 'waiting' &&
      target.errorMessage &&
      !target.errorMessage.startsWith('Waiting for provider deployment')
  );
  if (needsProviderCheck)
    return `${needsProviderCheck.label}: ${needsProviderCheck.errorMessage}`;

  const waiting = unfinished.find(
    (target) =>
      !['failed', 'cancelled', 'releasing'].includes(target.status) &&
      target.dependencyKeys.some((key) => {
        const dependency = byKey.get(key);
        return dependency && !finishedStatuses.has(dependency.status);
      })
  );
  if (waiting) {
    const dependencies = waiting.dependencyKeys
      .map((key) => byKey.get(key))
      .flatMap((dependency) =>
        dependency && !finishedStatuses.has(dependency.status)
          ? [dependency.label]
          : []
      );
    if (dependencies.length) {
      const prerequisite =
        dependencies.length === 1
          ? `${dependencies[0]} reaches its destination`
          : `${dependencies.join(' and ')} reach their destinations`;
      return waiting.rolloutStrategy === 'manual'
        ? `Approval for ${waiting.label} opens after ${prerequisite}.`
        : `${waiting.label} is waiting for ${dependencies.join(' and ')}.`;
    }
  }

  const manual = unfinished.find(
    (target) => target.rolloutStrategy === 'manual'
  );
  return manual
    ? `Approval needed for ${manual.label}.`
    : 'Check the release details to see what is holding it.';
}
